import { useCallback, useMemo } from "react";
import useLocalStorage from "hooks/useLocalStorage";
import LocalStorageHelper from "helpers/LocalStorageHelper";
import type { UpsellFeature } from "types/upsell";

const STORAGE_KEY_PREFIX = `${LocalStorageHelper.keys.upsellAccessRequested}:`;

/**
 * Remembers that an upgrade has already been requested for a given feature, so
 * the CTA can be disabled rather than letting a coach email support repeatedly.
 *
 * Keyed on the feature rather than the page's path: the two are one-to-one, but
 * the path is presentational and has already changed once, which would have
 * silently reset everyone's stored state.
 *
 * `useLocalStorage` broadcasts a `local-storage` event on write, so the banner
 * and CTA-panel buttons on the same page stay in step without shared state.
 */
function useUpsellAccessRequested(feature: UpsellFeature) {
  const [hasRequested, setHasRequested] = useLocalStorage(`${STORAGE_KEY_PREFIX}${feature}`, false);

  const markRequested = useCallback(() => setHasRequested(true), [setHasRequested]);

  return useMemo(() => ({ hasRequested, markRequested }), [hasRequested, markRequested]);
}

export default useUpsellAccessRequested;
